import { useCallback } from 'react';

import { type ResultType, toResultType } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import type { IIRevResultState } from '../../../interface/state/IRev';
import { type IUGRefId } from './helpers';

/**
 * The only four levels with a `get-by-referenceid` endpoint, and the action that serves each.
 *
 * This map is the single source of truth for "can this panel fetch that level?". EC8E and the
 * EC40* forms are perfectly valid `ResultType`s with **no read endpoint**, so a caller must be
 * able to distinguish them from a network failure — otherwise the UI blames the connection and
 * offers a retry that can never succeed.
 */
export const THUNK_BY_LEVEL = {
	EC8A: 'get_IrevPollingUnitResultByRefId',
	EC8B: 'get_IrevWardResultByRefId',
	EC8C: 'get_IrevLgaResultByRefId',
	EC8D: 'get_IrevStateResultByRefId',
} as const;

export type RefFetchableLevel = keyof typeof THUNK_BY_LEVEL;

/** Narrow a raw level string to one this panel can actually fetch, or null. */
export const toRefFetchableLevel = (value?: string | null): RefFetchableLevel | null => {
	const level = toResultType(value);
	return level && level in THUNK_BY_LEVEL ? (level as RefFetchableLevel) : null;
};

export const useGetResultsByRefID = ({
	rstype,
	onFailure,
	onSuccess,
}: {
	// Nullable by design: `rsProps.resultType` legitimately starts null, and callers used to hide
	// that with a `!` assertion. Accepting null lets them stop lying to the compiler.
	rstype: ResultType | null | undefined;
	onSuccess?: (res: IIRevResultState | undefined) => void;
	onFailure?: () => void;
}): IUGRefId => {
	const { actions, states } = useGlobalContext();

	// The level arrives lower case from the API (`assignment.resultType` is `Lowercase<ResultType>`)
	// and the map above is keyed upper case. Normalise once, here, so neither the dispatch nor the
	// store read can silently miss.
	const level = toRefFetchableLevel(rstype);

	const getResults = (referenceId: string) => {
		const thunk = level ? actions?.[THUNK_BY_LEVEL[level]] : undefined;
		if (!thunk) {
			// No endpoint for this level (or no level at all). Report it rather than returning
			// silently: a caller that never hears back shows a spinner forever. Exactly one of
			// onSuccess/onFailure fires for every call — that invariant is the fix.
			onFailure?.();
			return;
		}

		thunk({
			referenceId,
			onSuccess: (res) => {
				onSuccess?.(res);
			},
			onFailure,
		});
	};

	const getResponse = useCallback(() => {
		const stored = level ? states?._irev?.[THUNK_BY_LEVEL[level]] : undefined;
		return stored ?? ({} as IIRevResultState);
	}, [states?._irev, level]);

	return {
		getResults,
		response: getResponse(),
	};
};
